import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { Trash2, FolderTree, Download, Upload } from 'lucide-react'
import { ImportSyllabusModal } from '@/components/admin/ImportSyllabusModal'


interface ExamOption { id: number; name: string; code: string }
interface Syllabus {
  id: number
  name: string
  version: number
  description: string | null
  exam_id: number
  exam_name: string
  chapter_ids: number[]
}
interface Props {
  syllabi: Syllabus[]
  exams: ExamOption[]
}

export default function SyllabiIndex({ syllabi, exams }: Props) {
  const [name, setName] = useState('')
  const [version, setVersion] = useState(1)
  const [description, setDescription] = useState('')
  const [examId, setExamId] = useState<number | ''>(exams[0]?.id ?? '')
  const [importModalOpen, setImportModalOpen] = useState(false)


  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!examId || !name.trim()) return
    router.post('/admin/syllabi', {
      syllabus: {
        name: name.trim(),
        version,
        description: description || null,
        exam_id: examId,
      }
    }, {
      onSuccess: () => {
        setName('')
        setVersion(1)
        setDescription('')
      }
    })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure you want to delete this syllabus?')) return
    router.delete(`/admin/syllabi/${id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Taxonomy Management</h1>
          <p className="text-muted-foreground mt-1">Manage standards, subjects, chapters, sections, exams, and syllabi</p>
        </div>

        {/* Create form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Add Syllabus</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Target Exam *</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={examId}
                    onChange={(e) => setExamId(Number(e.target.value))}
                    required
                  >
                    <option value="" disabled>Select Exam *</option>
                    {exams.map((e) => (
                      <option key={e.id} value={e.id}>{e.name} ({e.code})</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-sm font-medium text-foreground">Syllabus Name *</label>
                  <Input
                    placeholder="e.g. JEE Main Official 2026"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Version *</label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="1"
                    value={version}
                    onChange={(e) => setVersion(Number(e.target.value))}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 pt-1">
                <div className="flex-1 max-w-xl">
                  <Input
                    placeholder="Optional description / notes"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={!name.trim() || !examId}>
                  Add Syllabus
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Syllabi ({syllabi.length})</CardTitle>
            <div className="flex items-center gap-2">
              {syllabi.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 font-medium text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete ALL ${syllabi.length} syllabi? This action cannot be undone.`)) {
                      router.delete('/admin/syllabi/destroy_all')
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 font-medium"
                onClick={() => setImportModalOpen(true)}
              >
                <Upload className="h-4 w-4 text-muted-foreground" />
                Import JSON
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 font-medium" asChild>
                <a href="/admin/syllabi/export">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  Export JSON
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Syllabus Name</TableHead>
                  <TableHead>Exam</TableHead>
                  <TableHead className="w-36">Chapter Count</TableHead>
                  <TableHead className="w-24">Version</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syllabi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No syllabi created yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  syllabi.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Link
                          href={`/admin/syllabi/${s.id}`}
                          className="font-medium text-primary hover:underline flex items-center gap-1.5"
                        >
                          <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
                          {s.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{s.exam_name}</TableCell>
                      <TableCell>{s.chapter_ids?.length ?? 0} chapters</TableCell>
                      <TableCell>v{s.version}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" asChild title="Manage Hierarchy">
                            <Link href={`/admin/syllabi/${s.id}`}>
                              <FolderTree className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(s.id)}
                            title="Delete Syllabus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>

            </Table>
          </CardContent>
        </Card>

        {/* Import Modal */}
        <ImportSyllabusModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
        />
      </div>
    </AppShell>
  )
}

